







































import {
  computed,
  defineComponent,
  ref,
  reactive,
  onMounted,
  onUpdated,
  nextTick,
} from '@nuxtjs/composition-api'
import debounce from 'lodash.debounce'
import { useEventListener } from '~/composables/ui/useEventListener'

export default defineComponent({
  name: 'BaseTouchScroll',

  props: {
    maxHeight: {
      type: String,
      default: '',
    },
    maxWidth: {
      type: String,
      default: '',
    },
    scrollbarVisible: {
      type: Boolean,
      default: false,
    },
    step: {
      type: Number,
      default: 0.25,
    },
    stepLength: {
      type: Number,
      default: 0,
    },
    scrollShadow: {
      type: Boolean,
      default: false,
    },
  },

  setup(props, { slots }) {
    const containerEl = ref<HTMLElement | null>(null)
    const state = reactive({
      scrollWidth: 0,
      offsetWidth: 0,
      clientWidth: 0,
      scrollHeight: 0,
      offsetHeight: 0,
      clientHeight: 0,
      scrollLeft: 0,
      scrollTop: 0,
    })

    const init = () => {
      if (containerEl.value) {
        const {
          scrollWidth,
          offsetWidth,
          clientWidth,
          scrollHeight,
          offsetHeight,
          clientHeight,
          scrollLeft,
          scrollTop,
        } = containerEl.value

        state.scrollWidth = scrollWidth
        state.offsetWidth = offsetWidth
        state.clientWidth = clientWidth
        state.scrollHeight = scrollHeight
        state.offsetHeight = offsetHeight
        state.clientHeight = clientHeight
        state.scrollLeft = scrollLeft
        state.scrollTop = scrollTop
      }
    }

    const xScrollable = computed(() => {
      return state.scrollWidth > state.clientWidth
    })

    const yScrollable = computed(() => {
      return state.scrollHeight > state.clientHeight
    })

    const scrollbarStepWidth = computed(() => {
      return xScrollable.value
        ? props.step * (state.scrollWidth - state.clientWidth)
        : 0
    })

    const scrollbarStepHeight = computed(() => {
      return yScrollable.value
        ? props.step * (state.scrollHeight - state.clientHeight)
        : 0
    })

    const scrollbarWidth = computed(() => {
      return yScrollable.value ? state.offsetWidth - state.clientWidth : 0
    })

    const scrollbarHeight = computed(() => {
      return xScrollable.value ? state.offsetHeight - state.clientHeight : 0
    })

    const hideScrollbarStyle = computed(() => {
      if (!props.scrollbarVisible) {
        return {
          marginRight: scrollbarWidth.value && `-${scrollbarWidth.value}px`,
          marginBottom: scrollbarHeight.value && `-${scrollbarHeight.value}px`,
        }
      }
    })

    let pos = { top: 0, left: 0, x: 0, y: 0 }

    const onMouseDown = (e: MouseEvent) => {
      if (containerEl.value && (xScrollable.value || yScrollable.value)) {
        containerEl.value.classList.remove('cursor-grab')
        containerEl.value.classList.add('cursor-grabbing', 'select-none')

        pos = {
          left: containerEl.value.scrollLeft,
          top: containerEl.value.scrollTop,
          x: e.clientX,
          y: e.clientY,
        }

        document.addEventListener('mousemove', onMouseMove)
        document.addEventListener('mouseup', onMouseUp)
      }
    }

    const onMouseMove = (e: MouseEvent) => {
      if (containerEl.value) {
        const dx = e.clientX - pos.x
        const dy = e.clientY - pos.y

        // Scroll the element
        containerEl.value.scrollTop = pos.top - dy
        containerEl.value.scrollLeft = pos.left - dx
      }
    }

    const onMouseUp = () => {
      if (containerEl.value) {
        containerEl.value.classList.remove('cursor-grabbing', 'select-none')
        containerEl.value.classList.add('cursor-grab')

        document.removeEventListener('mousemove', onMouseMove)
        document.removeEventListener('mouseup', onMouseUp)
      }
    }

    const onScroll = () => {
      if (containerEl.value) {
        state.scrollLeft = containerEl.value.scrollLeft
        state.scrollTop = containerEl.value.scrollTop
      }
    }

    const scrollLeft = () => {
      if (containerEl.value && xScrollable.value) {
        containerEl.value.scrollLeft -=
          props.stepLength || scrollbarStepWidth.value
      }
    }

    const scrollRight = () => {
      if (containerEl.value && xScrollable.value) {
        containerEl.value.scrollLeft +=
          props.stepLength || scrollbarStepWidth.value
      }
    }

    const scrollTop = () => {
      if (containerEl.value && yScrollable.value) {
        containerEl.value.scrollTop -=
          props.stepLength || scrollbarStepHeight.value
      }
    }

    const scrollBottom = () => {
      if (containerEl.value && yScrollable.value) {
        containerEl.value.scrollTop +=
          props.stepLength || scrollbarStepHeight.value
      }
    }

    onMounted(() => init())

    onUpdated(() => {
      nextTick(() => init())
    })

    if (process.client) {
      useEventListener(containerEl, 'mousedown', onMouseDown)
      useEventListener(window, 'resize', debounce(init, 150))
      useEventListener(containerEl, 'scroll', onScroll)
    }

    return {
      slots,
      containerEl,
      xScrollable,
      yScrollable,
      scrollbarWidth,
      scrollbarHeight,
      state,
      hideScrollbarStyle,

      scrollLeft,
      scrollRight,
      scrollTop,
      scrollBottom,
    }
  },
})
